import { useState, useEffect, useRef } from "react";
import {
  EditableInputComponent,
  EditableSelectComponent,
  EditableTextareaComponent,
  SectionHeader,
} from "components";
import { Document } from "assets/icons"; // Replace with appropriate icons
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { FileLabel } from "shared/types/file";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import { BuildingDTO } from "shared/types/building";
import {
  CommonInstallationsInfoDTO,
  ElevatorPresentStatus,
} from "shared/types/common-installations";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // Base styling

// Type assertion for React 18 compatibility
const DatePicker = ReactDatePicker as any;

import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import FileLogTable from "shared/components/uploadFileModal/fileLogTable";

interface ElevatorInfoComponentProps {
  commonInstallationsInfo: CommonInstallationsInfoDTO;
  handleCommonInstallationsInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  building: BuildingDTO;
  parentEditMode?: boolean;
}

const ElevatorInfoComponent = ({
  commonInstallationsInfo,
  handleCloseParent,
  handleSaveInParent,
  building,
  handleCommonInstallationsInfoChangeInParent,
  parentEditMode = true,
}: ElevatorInfoComponentProps) => {
  const [editMode, setEditMode] = useState(parentEditMode);
  const [editedCommonInstallationsInfo, setEditedCommonInstallationsInfo] =
    useState(commonInstallationsInfo);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { session } = useSession();
  const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
  const [selectedFileLabel, setSelectedFileLabel] = useState<FileLabel | null>(
    null,
  );
  const fileLogTableRef = useRef<{ saveChanges: () => Promise<void>; hasUnsavedChanges: boolean } | null>(null);

  const elevatorFileLabels = [
    FileLabel.ElevatorInspectionCertificate,
    FileLabel.ElevatorRiskAnalysis,
    FileLabel.ElevatorMaintenanceContract,
  ];

  useEffect(() => {
    setEditedCommonInstallationsInfo(commonInstallationsInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch
  
  const getDateFieldStatusColor = (dateString: any) => {
    if (!dateString) {
      return "bg-transparent-500";
    }

    const currentDate = new Date();
    const targetDate = new Date(dateString);
    const fourMonthsBeforeTarget = new Date(targetDate);
    fourMonthsBeforeTarget.setMonth(targetDate.getMonth() - 4);

    if (currentDate > targetDate) {
      return "bg-red-500";
    } else if (
      currentDate >= fourMonthsBeforeTarget &&
      currentDate <= targetDate
    ) {
      return "bg-orange-500";
    } else if (currentDate < fourMonthsBeforeTarget) {
      return "bg-green-500";
    } else {
      return "bg-transparent-500";
    }
  };

  const getElevatorPresentStatusColor = (status: any) => {
    switch (status) {
      case ElevatorPresentStatus.HAS_ELEVATOR:
      case ElevatorPresentStatus.NO_ELEVATOR:
        return "bg-green-500"; // Green for OK
      case ElevatorPresentStatus.HAS_ELEVATOR_OUT_SERVICE:
        return "bg-orange-500"; // Yellow for Partial
      default:
        return "bg-transparent-500";
    }
  };

  const handleFileModalClose = async () => {
    setSelectedFileLabel(null);
    setFileModalOpen(false);
  };

  const handleFileUploadSuccess = async (fileLogData: any) => {
    const { fileLog } = fileLogData;

    let updatedField = {};

    switch (selectedFileLabel) {
      case FileLabel.ElevatorInspectionCertificate:
        updatedField = {
          elevatorInspectionCertificateFileLogId: fileLog.id,
          elevatorInspectionCertificateFileLog: fileLog,
        };
        break;
      case FileLabel.ElevatorRiskAnalysis:
        updatedField = {
          elevatorRiskAnalysisFileLogId: fileLog.id,
          elevatorRiskAnalysisFileLog: fileLog,
        };
        break;
      case FileLabel.ElevatorMaintenanceContract:
        updatedField = {
          elevatorMaintenanceContractFileLogId: fileLog.id,
          elevatorMaintenanceContractFileLog: fileLog,
        };
        break;
      // Add more cases for other file labels if needed
      default:
        return;
    }

    // Update the state with the new file log information
    setEditedCommonInstallationsInfo((prev: any) => ({
      ...prev,
      ...updatedField,
    }));

    // Indicate that changes have been made
    setHasUnsavedChanges(true);

    // Optionally, close the file upload modal
    setFileModalOpen(false);

    // Show a success message
    toast.success("File uploaded successfully!");

    // Update the parent component, if needed
    if (handleCommonInstallationsInfoChangeInParent) {
      handleCommonInstallationsInfoChangeInParent({
        ...commonInstallationsInfo,
        ...updatedField,
      });
    }
  };

  const updateCommonInstallationsInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedCommonInstallationsInfo((prev: any) => ({
      ...prev,
      ...updatedFields,
    }));

    if (handleCommonInstallationsInfoChangeInParent) {
      handleCommonInstallationsInfoChangeInParent({
        ...commonInstallationsInfo,
        ...updatedFields,
      });
    }
  };

  // For example, handleSave:
  const handleSave = async () => {
    setHasUnsavedChanges(false);
    try {
      // First save the file log changes if there are any
      if (fileLogTableRef.current?.hasUnsavedChanges) {
        await fileLogTableRef.current.saveChanges();
      }
      
      if (handleSaveInParent) {
        handleSaveInParent();
      } else {
        await fetchWithAuth(
          `${API_URL}/building/elevator-info`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(editedCommonInstallationsInfo),
          },
          session,
        );
        toast.success("Common installations information successfully updated!");
      }
    } catch (e) {
      console.error(e);
      toast.error(
        "An error occurred while updating common installations information.",
      );
    }
  };

  return (
    <section className="flex flex-col gap-0 rounded-md bg-white">
      <SectionHeader
        title="Gemeenschappelijke Installaties Informatie"
        icon={<Document />}
        editMode={editMode}
        onEditClick={() => setEditMode(!editMode)}
        onSaveClick={handleSave}
        sticky={false}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges || fileLogTableRef.current?.hasUnsavedChanges}
        onCloseClick={() => {
          if (handleCloseParent) handleCloseParent();
        }}
        hideCloseAction={true}
      />
      <div className="p-3">
        <div className="gap-3 sm:grid sm:grid-cols-2">
          {/* Elevator Present Status */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getElevatorPresentStatusColor(
                  editedCommonInstallationsInfo &&
                    editedCommonInstallationsInfo?.elevatorPresentStatus,
                )}`}
              />
              <p>Is er een lift aanwezig?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze"
              value={
                (editedCommonInstallationsInfo &&
                  editedCommonInstallationsInfo?.elevatorPresentStatus) ||
                ""
              }
              isLoading={false}
              options={Object.values(ElevatorPresentStatus).map((value) => ({
                label: value,
                value,
              }))}
              editMode={editMode}
              onChange={(value) =>
                updateCommonInstallationsInfo({ elevatorPresentStatus: value })
              }
            />
          </div>

          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getElevatorPresentStatusColor(
                  editedCommonInstallationsInfo &&
                    editedCommonInstallationsInfo?.elevatorPresentStatus,
                )}`}
              />
              <p>Type lift (koppeling met backend nodig)</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze"
              value={
                (editedCommonInstallationsInfo &&
                  editedCommonInstallationsInfo?.elevatorPresentStatus) ||
                ""
              }
              isLoading={false}
              options={Object.values(ElevatorPresentStatus).map((value) => ({
                label: value,
                value,
              }))}
              editMode={editMode}
              onChange={(value) =>
                updateCommonInstallationsInfo({ elevatorPresentStatus: value })
              }
            />
          </div>

          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getElevatorPresentStatusColor(
                  editedCommonInstallationsInfo &&
                    editedCommonInstallationsInfo?.elevatorPresentStatus,
                )}`}
              />
              <p>Max. aantal personen (koppeling met backend nodig)</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze"
              value={
                (editedCommonInstallationsInfo &&
                  editedCommonInstallationsInfo?.elevatorPresentStatus) ||
                ""
              }
              isLoading={false}
              options={Object.values(ElevatorPresentStatus).map((value) => ({
                label: value,
                value,
              }))}
              editMode={editMode}
              onChange={(value) =>
                updateCommonInstallationsInfo({ elevatorPresentStatus: value })
              }
            />
          </div>

          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getElevatorPresentStatusColor(
                  editedCommonInstallationsInfo &&
                    editedCommonInstallationsInfo?.elevatorPresentStatus,
                )}`}
              />
              <p>Max. gewicht (koppeling met backend nodig)</p>
            </header>
            <EditableInputComponent
              label="Gewicht in kg"
              value={
                (editedCommonInstallationsInfo &&
                  editedCommonInstallationsInfo?.elevatorPresentStatus) ||
                ""
              }
              isLoading={false}
              editMode={editMode}
              onChange={(value) =>
                updateCommonInstallationsInfo({ elevatorPresentStatus: value })
              }
            />
          </div>

          {/* Elevator Remarks */}
          <EditableTextareaComponent
            label="Opmerkingen Lift"
            value={editedCommonInstallationsInfo.elevatorRemarks || ""}
            editMode={editMode}
            isLoading={false}
            onChange={(value) =>
              updateCommonInstallationsInfo({ elevatorRemarks: value })
            }
          />

          {editedCommonInstallationsInfo?.elevatorPresentStatus ==
            ElevatorPresentStatus.HAS_ELEVATOR && (
            <>
              {/* Elevator Documents */}
              <div className="col-span-2 flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                <header className="flex items-center gap-2 text-sm font-medium">
                  <p>Liftdocumenten</p>
                </header>
                <FileLogTable
                  ref={fileLogTableRef}
                  acceptedLabels={elevatorFileLabels}
                  onUploadClick={() => setFileModalOpen(true)}
                  building={building || undefined}
                />
              </div>

              {/* Elevator Contract Termination Date */}
              <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                <header className="flex items-center gap-2 text-sm font-medium">
                  <span
                    className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getDateFieldStatusColor(
                      editedCommonInstallationsInfo.elevatorContractTerminationDate,
                    )}`}
                  />
                  <p>Opzegdatum Contract Lift</p>
                </header>
                <DatePicker
                  selected={
                    editedCommonInstallationsInfo.elevatorContractTerminationDate
                      ? new Date(
                          editedCommonInstallationsInfo.elevatorContractTerminationDate,
                        )
                      : null
                  }
                  onChange={(date: any) =>
                    updateCommonInstallationsInfo({
                      elevatorContractTerminationDate: date,
                    })
                  }
                  dateFormat="yyyy/MM/dd"
                  className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
                />
              </div>

              {/* Last Elevator Maintenance Date */}
              <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                <header className="flex items-center gap-2 text-sm font-medium">
                  <span
                    className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getDateFieldStatusColor(
                      editedCommonInstallationsInfo.lastElevatorMaintenanceDate,
                    )}`}
                  />
                  <p>Laatste Onderhoudsdatum Lift</p>
                </header>
                <DatePicker
                  selected={
                    editedCommonInstallationsInfo.lastElevatorMaintenanceDate
                      ? new Date(
                          editedCommonInstallationsInfo.lastElevatorMaintenanceDate,
                        )
                      : null
                  }
                  onChange={(date: any) =>
                    updateCommonInstallationsInfo({
                      lastElevatorMaintenanceDate: date,
                    })
                  }
                  dateFormat="yyyy/MM/dd"
                  className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element"
                />
              </div>

              {/* Next Elevator Maintenance Date */}
              <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                <header className="flex items-center gap-2 text-sm font-medium">
                  <span
                    className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getDateFieldStatusColor(
                      editedCommonInstallationsInfo.nextElevatorMaintenanceDate,
                    )}`}
                  />
                  <p>Volgende Onderhoudsdatum Lift</p>
                </header>
                <DatePicker
                  selected={
                    editedCommonInstallationsInfo.nextElevatorMaintenanceDate
                      ? new Date(
                          editedCommonInstallationsInfo.nextElevatorMaintenanceDate,
                        )
                      : null
                  }
                  onChange={(date: any) =>
                    updateCommonInstallationsInfo({
                      nextElevatorMaintenanceDate: date,
                    })
                  }
                  dateFormat="yyyy/MM/dd"
                  className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element"
                />
              </div>

              {/* Elevator Year of Manufacture */}
              <EditableInputComponent
                label="Bouwjaar Lift"
                value={
                  (editedCommonInstallationsInfo.elevatorYearOfManufacture as any) ||
                  ""
                }
                editMode={editMode}
                isLoading={false}
                onChange={(value) =>
                  updateCommonInstallationsInfo({
                    elevatorYearOfManufacture: value,
                  })
                }
              />

              {/* Elevator Brand */}
              <EditableInputComponent
                label="Merk Lift"
                value={editedCommonInstallationsInfo.elevatorBrand || ""}
                editMode={editMode}
                isLoading={false}
                onChange={(value) =>
                  updateCommonInstallationsInfo({ elevatorBrand: value })
                }
              />
            </>
          )}
          <ImagesContainer
            buildingId={building.id as string}
            fileLabel={FileLabel.ElevatorPicture}
          />

          {/* Modal for File Upload */}
          <ModalUploadFileLog
            isOpen={fileModalOpen}
            onClose={handleFileModalClose}
            building={building}
            customLabels={elevatorFileLabels}
            onFileUploadSuccess={handleFileUploadSuccess}
          />
        </div>
      </div>
    </section>
  );
};

export default ElevatorInfoComponent;
